import { GetCurrentMember } from 'iam/SessionStore'
import { GetTeamMember } from 'iam/TeamMemberStore'
import { GetJob } from 'jobs/JobStore'
import { CallSourceFactory } from 'messaging/calling/CallSourceFactory'
import VideoCallStore from 'messaging/calling/VideoCallStore'
import { CallPorts } from 'messaging/calling/ports'
import Conversation from 'messaging/models/Conversation'
import { Observable } from 'rxjs/internal/Observable'
import SoundPlayer, { SoundId } from 'sound/SoundPlayer'
import CallDeviceManager from './CallDeviceManager'

/**
 * Neatly wraps stuff needed for calling.
 */
export function VideoCallAdapter(
  callStore: VideoCallStore,
  soundPlayer: SoundPlayer,
  getNow: () => Date,
  hangupSignal$: Observable<unknown>,
  navigateToConversation: (conversation: Conversation) => void,
  callPermissionsAdapter: ConversationCallPermissionsPorts,
  callDeviceManager: CallDeviceManager
): CallPorts {
  return {
    getNow,
    hangupSignal$,
    incomingCall$: callStore.incomingCall$,
    acquireCallSource: CallSourceFactory(),
    getSounds: () => ({
      incoming: soundPlayer.getSound(SoundId.IncomingCall),
      ringing: soundPlayer.getSound(SoundId.RingingCall),
      joined: soundPlayer.getSound(SoundId.GotMessage),
    }),
    showCallContext: (call) => {
      navigateToConversation(call.conversation)
    },

    activateDeviceManager: () => callDeviceManager.activate(),
    selectMediaDevice: (device) => callDeviceManager.selectMediaDevice(device),
    mediaDeviceChanged$: callDeviceManager.mediaDeviceChanged$,
    selectedDevices$: callDeviceManager.selectedDevices$,
    mediaDevices$: callDeviceManager.mediaDevices$,

    async start(conversation, invitees) {
      return callStore.startCall(conversation, invitees)
    },
    async join(call) {
      return callStore.joinCall(call)
    },
    async fetch(call) {
      await callStore.fetchCall(call.id)
    },
    async hangup(call): Promise<void> {
      await callStore.hangup(call)
    },
    async decline(call): Promise<void> {
      await callStore.decline(call)
    },
    canHaveOutgoingVideo(call) {
      return callPermissionsAdapter.canHaveOutgoingVideo(call.conversation)
    },
  }
}

/**
 * Permission ports for Calls based on the underlying Conversation.
 */
interface ConversationCallPermissionsPorts {
  /**
   * Whether the current user in the specified conversation can
   * have outgoing video.
   *
   * @param conversation
   */
  canHaveOutgoingVideo: (conversation: Conversation) => boolean
}

export function ConversationCallPermissionsAdapter(
  getCurrentMember: GetCurrentMember,
  getTeamMember: GetTeamMember,
  getJob: GetJob
): ConversationCallPermissionsPorts {
  return {
    canHaveOutgoingVideo(conversation: Conversation) {
      const currentMember = getCurrentMember()
      // If the workspace permission allows camera, then we allow it and exit.
      if (currentMember.hasPermission('CALL_ENABLE_CAMERA')) {
        return true
      }

      // Check if we have a job associated with the conversation and
      // check the team permissions on the teams associated with the job.
      const jobId =
        conversation.meta.type === 'Job' ? conversation.meta.id : null

      if (!jobId) {
        return false
      }

      const job = getJob(jobId)
      if (!job?.teams.length) {
        return false
      }

      return job.teams.some((team) => {
        const teamMember = getTeamMember(team.id, currentMember.user.publicId)
        return teamMember?.hasPermission('CALL_ENABLE_CAMERA')
      })
    },
  }
}
